<template>
  <div>
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Vendor Directory</h5>
        <p class="page-sub">Manage and qualify vendors for procurement</p>
      </div>
      <div class="header-actions">
        <div class="export-wrap" ref="exportWrap">
          <button class="btn-export" @click="exportMenuOpen = !exportMenuOpen" :disabled="exporting">
            <font-awesome-icon icon="download" /> Export <font-awesome-icon icon="chevron-down" style="font-size:10px" />
          </button>
          <div v-if="exportMenuOpen" class="export-menu">
            <button class="export-opt" @click="openExportModal('csv')"><font-awesome-icon icon="file-csv" class="me-2" />Export CSV</button>
            <button class="export-opt" @click="openExportModal('pdf')"><font-awesome-icon icon="file-pdf" class="me-2" />Export PDF</button>
          </div>
        </div>
        <button
          v-if="permission?.view_procurement_categories || permission?.manage_procurement_categories"
          class="btn-outline"
          @click="$router.push($url('/procurement/vendor-categories'))"
        >
          <font-awesome-icon icon="tags" class="me-1" /> Manage Categories
        </button>
        <button v-if="permission?.manage_vendors && activeView === 'active'" class="btn-primary" @click="openCreate">
          <font-awesome-icon icon="plus" class="me-1" /> Add Vendor
        </button>
      </div>
    </div>

    <!-- Vendor Portal URL bar -->
    <div class="portal-bar">
      <font-awesome-icon icon="link" class="portal-bar-icon" />
      <span class="portal-bar-label">Vendor Portal:</span>
      <span class="portal-bar-url">{{ portalUrl }}</span>
      <button class="portal-bar-copy" @click="copyPortalUrl" :class="{ 'portal-bar-copy--done': copied }" :title="copied ? 'Copied!' : 'Copy link'">
        <font-awesome-icon :icon="copied ? 'check' : 'copy'" />
        {{ copied ? 'Copied!' : 'Copy' }}
      </button>
    </div>

    <!-- View Tabs -->
    <div class="view-tabs">
      <button :class="['view-tab', { 'view-tab--active': activeView === 'active' }]" @click="switchView('active')">
        <font-awesome-icon icon="list" class="me-1" /> Active Vendors
      </button>
      <button :class="['view-tab', { 'view-tab--active': activeView === 'trash' }]" @click="switchView('trash')">
        <font-awesome-icon icon="trash" class="me-1" /> Deleted
      </button>
    </div>

    <!-- Filters (active view only) -->
    <div v-if="activeView === 'active'" class="filter-bar">
      <input v-model="search" @input="debouncedFetch" class="filter-input" placeholder="Search name, email, vendor number..." />
      <select v-model="filterStatus" @change="fetchVendors" class="filter-select">
        <option value="">All Statuses</option>
        <option value="prospect">Prospect</option>
        <option value="registered">Registered</option>
        <option value="due_diligence">Due Diligence</option>
        <option value="qualified">Qualified</option>
        <option value="approved">Approved</option>
        <option value="suspended">Suspended</option>
        <option value="blocked">Blocked</option>
        <option value="in_workflow">In Workflow</option>
        <option value="rejected">Rejected</option>
      </select>
      <input type="date" v-model="dateFrom" class="filter-input" style="max-width:160px" />
      <input type="date" v-model="dateTo" class="filter-input" style="max-width:160px" />
      <button class="btn-apply" @click="applyDateFilter">Apply</button>
      <button class="btn-clear" @click="clearDateFilter" v-if="dateFrom || dateTo">Clear</button>
      <div class="per-page-wrap">
        <label class="per-page-label">Show</label>
        <select class="per-page-select" v-model.number="perPage" @change="page = 1; fetchVendors()">
          <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
        </select>
        <span class="per-page-label">per page</span>
      </div>
    </div>

    <!-- Active Vendors Table -->
    <div v-if="activeView === 'active'" class="card">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading vendors...</p>
      </div>

      <div v-else-if="!vendors.length" class="empty-state">
        <font-awesome-icon icon="building" class="empty-icon" />
        <p>No vendors found</p>
        <span>Add your first vendor to get started</span>
      </div>

      <table v-else class="data-table">
        <thead>
          <tr>
            <th>Vendor #</th>
            <th>Name</th>
            <th>Primary Contact</th>
            <th>Categories</th>
            <th>Status</th>
            <th>Risk</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="v in vendors" :key="v.id" @click="goToProfile(v.id)" class="row-link">
            <td><span class="mono">{{ v.vendor_number }}</span></td>
            <td>
              <div class="vendor-name">{{ v.name }}</div>
              <div class="vendor-sub" v-if="v.trading_name">{{ v.trading_name }}</div>
            </td>
            <td>
              <div v-if="v.primary_contact">
                <div>{{ v.primary_contact.name }}</div>
                <div class="vendor-sub">{{ v.primary_contact.email }}</div>
              </div>
              <span v-else class="text-muted">—</span>
            </td>
            <td>
              <span class="cat-badge" v-for="c in (v.categories || []).slice(0, 2)" :key="c.id">{{ c.name }}</span>
              <span class="cat-more" v-if="(v.categories || []).length > 2">+{{ v.categories.length - 2 }}</span>
            </td>
            <td><span :class="['status-badge', `status-badge--${v.status}`]">{{ formatStatus(v.status) }}</span></td>
            <td>
              <span v-if="v.risk_rating" :class="['risk-badge', `risk-badge--${v.risk_rating}`]">{{ v.risk_rating }}</span>
              <span v-else class="text-muted">—</span>
            </td>
            <td @click.stop>
              <div class="action-menu">
                <button class="btn-icon" @click="goToProfile(v.id)" title="View profile">
                  <font-awesome-icon icon="eye" />
                </button>
                <button class="btn-icon" @click="openEdit(v)" title="Edit">
                  <font-awesome-icon icon="edit" />
                </button>
                <button v-if="v.status === 'approved'" class="btn-icon" @click="openChangePassword(v)" title="Change password">
                  <font-awesome-icon icon="key" />
                </button>
                <button class="btn-icon btn-icon--danger" @click="confirmDelete(v)" title="Delete">
                  <font-awesome-icon icon="trash" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination-bar" v-if="meta && meta.last_page > 1">
        <span class="pagination-info">Showing {{ meta.from }}–{{ meta.to }} of {{ meta.total }}</span>
        <div class="pagination-controls">
          <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(1)">«</button>
          <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(meta.current_page - 1)">‹ Prev</button>
          <span class="pg-pages">
            <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === meta.current_page }" @click="changePage(p)">{{ p }}</button>
          </span>
          <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.current_page + 1)">Next ›</button>
          <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.last_page)">»</button>
        </div>
      </div>
    </div>

    <!-- Deleted Vendors Table -->
    <div v-if="activeView === 'trash'" class="card">
      <div v-if="trashedLoading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading deleted vendors...</p>
      </div>
      <div v-else-if="!trashedVendors.length" class="empty-state">
        <font-awesome-icon icon="trash" class="empty-icon" />
        <p>No deleted vendors</p>
        <span>Deleted vendors will appear here and can be restored.</span>
      </div>
      <table v-else class="data-table">
        <thead>
          <tr>
            <th>Vendor No.</th>
            <th>Name</th>
            <th>Email</th>
            <th>Status at deletion</th>
            <th>Deleted</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="v in trashedVendors" :key="v.id">
            <td><span class="mono">{{ v.vendor_number }}</span></td>
            <td>
              <div class="vendor-name">{{ v.name }}</div>
              <div class="vendor-sub" v-if="v.trading_name">{{ v.trading_name }}</div>
            </td>
            <td>{{ v.email || '—' }}</td>
            <td><span :class="['status-badge', `status-badge--${v.status}`]">{{ formatStatus(v.status) }}</span></td>
            <td class="text-muted">{{ formatDate(v.deleted_at) }}</td>
            <td>
              <div class="action-menu">
                <button class="btn-restore" :disabled="restoringId === v.id" @click="restoreVendor(v)" title="Restore">
                  <font-awesome-icon icon="rotate-left" /> {{ restoringId === v.id ? 'Restoring…' : 'Restore' }}
                </button>
                <button class="btn-icon btn-icon--danger" @click="confirmForceDelete(v)" title="Delete permanently">
                  <font-awesome-icon icon="trash" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="pagination" v-if="trashedMeta && trashedMeta.last_page > 1">
        <button :disabled="trashedMeta.current_page === 1" @click="changeTrashPage(trashedMeta.current_page - 1)" class="page-btn">&#8592;</button>
        <span class="page-info">Page {{ trashedMeta.current_page }} of {{ trashedMeta.last_page }}</span>
        <button :disabled="trashedMeta.current_page === trashedMeta.last_page" @click="changeTrashPage(trashedMeta.current_page + 1)" class="page-btn">&#8594;</button>
      </div>
    </div>

    <!-- Force Delete Confirm Modal -->
    <div v-if="forceDeleteTarget" class="modal-backdrop" @click.self="forceDeleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6>Permanently Delete Vendor</h6>
          <button class="btn-close-modal" @click="forceDeleteTarget = null">&times;</button>
        </div>
        <div class="modal-body">
          <p>This will <strong>permanently delete</strong> <strong>{{ forceDeleteTarget.name }}</strong> and all associated data.</p>
          <p style="color:#dc2626;font-size:13px;margin-top:6px;">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="forceDeleteTarget = null">Cancel</button>
          <button class="btn-danger" :disabled="forceDeleting" @click="forceDeleteVendor">
            {{ forceDeleting ? 'Deleting…' : 'Delete Permanently' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Create / Edit Modal -->
    <div v-if="modalVisible" class="modal-backdrop" @click.self="closeModal">
      <div class="modal-box">
        <div class="modal-header">
          <h6>{{ editingId ? 'Edit Vendor' : 'Add Vendor' }}</h6>
          <button class="btn-close-modal" @click="closeModal">&times;</button>
        </div>

        <div class="modal-body">
          <div class="field-grid">
            <div class="field-group field-full">
              <label class="field-label">Vendor Name <span class="req">*</span></label>
              <input v-model="form.name" class="field-input" :class="{ 'field-input--error': errors.name }" placeholder="Legal business name" />
              <span class="field-error" v-if="errors.name">{{ errors.name }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Trading Name</label>
              <input v-model="form.trading_name" class="field-input" placeholder="Trading / DBA name" />
            </div>
            <div class="field-group">
              <label class="field-label">Status</label>
              <select v-model="form.status" class="field-input field-select">
                <option value="prospect">Prospect</option>
                <option value="registered">Registered</option>
                <option value="due_diligence">Due Diligence</option>
                <option value="qualified">Qualified</option>
                <option value="approved">Approved</option>
                <option value="suspended">Suspended</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Email <span class="req">*</span></label>
              <input v-model="form.email" type="email" class="field-input" :class="{ 'field-input--error': errors.email }" placeholder="vendor@company.com" />
              <span class="field-error" v-if="errors.email">{{ errors.email }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Phone</label>
              <input v-model="form.phone" class="field-input" placeholder="Phone number" />
            </div>
            <div class="field-group">
              <label class="field-label">Tax ID</label>
              <input v-model="form.tax_id" class="field-input" placeholder="Tax / VAT number" />
            </div>
            <div class="field-group">
              <label class="field-label">RC Number</label>
              <input v-model="form.rc_number" class="field-input" placeholder="Registration number" />
            </div>
            <div class="field-group">
              <label class="field-label">Risk Rating</label>
              <select v-model="form.risk_rating" class="field-input field-select">
                <option value="">None</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Website</label>
              <input v-model="form.website" class="field-input" placeholder="https://..." />
            </div>
            <div class="field-group">
              <label class="field-label">Country</label>
              <input v-model="form.country" class="field-input" placeholder="Country" />
            </div>
            <div class="field-group">
              <label class="field-label">City</label>
              <input v-model="form.city" class="field-input" placeholder="City" />
            </div>
            <div class="field-group field-full">
              <label class="field-label">Address</label>
              <input v-model="form.address" class="field-input" placeholder="Full business address" />
            </div>
            <div class="field-group field-full">
              <label class="field-label">Categories <span v-if="!editingId" class="req">*</span></label>
              <div class="cat-checkbox-list" :class="{ 'cat-checkbox-list--error': errors.category_ids }">
                <label v-for="cat in categories" :key="cat.id" class="cat-checkbox">
                  <input type="checkbox" :value="cat.id" v-model="form.category_ids" />
                  {{ cat.name }}
                </label>
                <span v-if="!categories.length" class="text-muted">No categories — add them via Manage Categories</span>
              </div>
              <span class="field-error" v-if="errors.category_ids">{{ errors.category_ids }}</span>
            </div>
            <div class="field-group field-full">
              <label class="field-label">Notes</label>
              <textarea v-model="form.notes" class="field-input" rows="3" placeholder="Internal notes..."></textarea>
            </div>
          </div>

          <!-- Bank Details Section -->
          <div class="section-divider">
            <span class="section-divider-label">Bank Details</span>
          </div>
          <div class="field-grid">
            <div class="field-group">
              <label class="field-label">Bank Name</label>
              <input v-model="form.bank_name" class="field-input" placeholder="e.g. First Bank, Barclays" />
            </div>
            <div class="field-group">
              <label class="field-label">Account Name</label>
              <input v-model="form.bank_account_name" class="field-input" placeholder="Name on account" />
            </div>
            <div class="field-group">
              <label class="field-label">Account Number</label>
              <input v-model="form.bank_account_number" class="field-input" placeholder="Account number" />
            </div>
            <div class="field-group">
              <label class="field-label">Sort Code</label>
              <input v-model="form.bank_sort_code" class="field-input" placeholder="e.g. 00-00-00" />
            </div>
            <div class="field-group">
              <label class="field-label">SWIFT / BIC</label>
              <input v-model="form.bank_swift_code" class="field-input" placeholder="SWIFT / BIC code" />
            </div>
            <div class="field-group">
              <label class="field-label">IBAN</label>
              <input v-model="form.bank_iban" class="field-input" placeholder="IBAN" />
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-outline" @click="closeModal">Cancel</button>
          <button class="btn-primary" :disabled="saving" @click="saveVendor">
            <span v-if="saving" class="spinner-sm"></span>
            {{ saving ? 'Saving...' : (editingId ? 'Update Vendor' : 'Create Vendor') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Change Password Modal -->
    <div v-if="changePasswordTarget" class="modal-backdrop" @click.self="closeChangePassword">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6>Change Vendor Password</h6>
          <button class="btn-close-modal" @click="closeChangePassword">&times;</button>
        </div>
        <div class="modal-body">
          <p class="pw-vendor-name">{{ changePasswordTarget.name }}</p>
          <div v-if="changePwErrors.length" class="error-box">
            <div v-for="(e, i) in changePwErrors" :key="i" class="error-item">{{ e }}</div>
          </div>
          <div class="field-group" style="margin-bottom:14px;">
            <label class="field-label" style="display:flex;align-items:center;justify-content:space-between;">
              <span>New Password <span class="req">*</span></span>
              <button type="button" class="btn-regenerate" @click="generatePassword" title="Generate new password">
                <font-awesome-icon icon="rotate" /> Regenerate
              </button>
            </label>
            <input v-model="changePwForm.password" type="text" class="field-input field-input--mono" autocomplete="new-password" />
          </div>
          <div class="field-group">
            <label class="field-label">Confirm Password <span class="req">*</span></label>
            <input v-model="changePwForm.password_confirmation" type="text" class="field-input field-input--mono" autocomplete="new-password" />
          </div>
          <p class="pw-mail-note">
            <font-awesome-icon icon="envelope" /> A copy of the new password will be emailed to <strong>{{ changePasswordTarget.email }}</strong>
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="closeChangePassword">Cancel</button>
          <button class="btn-primary" :disabled="changePwLoading" @click="submitChangePassword">
            <span v-if="changePwLoading" class="spinner-sm"></span>
            {{ changePwLoading ? 'Saving...' : 'Update Password' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Export Column Picker ── -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-ghost-cp" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary-cp" :disabled="selectedExportCols.length === 0 || exporting" @click="doExport()">
              <font-awesome-icon :icon="exporting ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exporting" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Delete Confirm Modal -->
    <div v-if="deleteTarget" class="modal-backdrop" @click.self="deleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6>Delete Vendor</h6>
          <button class="btn-close-modal" @click="deleteTarget = null">&times;</button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete <strong>{{ deleteTarget.name }}</strong>? This cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" :disabled="deleting" @click="deleteVendor">
            {{ deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  name: 'VendorDirectory',

  data() {
    return {
      vendors: [],
      categories: [],
      meta: null,
      loading: false,
      search: '',
      filterStatus: '',
      page: 1,
      perPage: 5,
      dateFrom: '', dateTo: '', exportMenuOpen: false, exporting: false,
      showExportModal: false, pendingExportFmt: 'csv', selectedExportCols: [],
      exportColumnDefs: [
        { key: 'vendor_no',     label: 'Vendor No',    pdfWidth: 70  },
        { key: 'name',          label: 'Name',          pdfWidth: 120 },
        { key: 'trading_name',  label: 'Trading Name',  pdfWidth: 100 },
        { key: 'email',         label: 'Email',         pdfWidth: 130 },
        { key: 'phone',         label: 'Phone',         pdfWidth: 80  },
        { key: 'status',        label: 'Status',        pdfWidth: 80  },
        { key: 'risk',          label: 'Risk',          pdfWidth: 55  },
        { key: 'categories',    label: 'Categories',    pdfWidth: 126 },
      ],

      modalVisible: false,
      editingId: null,
      saving: false,
      errors: {},
      form: this.blankForm(),

      deleteTarget: null,
      deleting: false,

      activeView: 'active',
      trashedVendors: [],
      trashedMeta: null,
      trashedLoading: false,
      trashPage: 1,
      forceDeleteTarget: null,
      forceDeleting: false,
      restoringId: null,

      searchTimer: null,
      copied: false,

      changePasswordTarget: null,
      changePwForm: { password: '', password_confirmation: '' },
      changePwLoading: false,
      changePwErrors: [],
    };
  },

  setup() {
    return { toast: useToast(), store: useStore() };
  },

  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
    portalUrl() {
      const code = this.$route?.params?.companyCode || localStorage.getItem('companyCode') || '';
      return code
        ? `${window.location.origin}/vendor/${code}/login`
        : `${window.location.origin}/vendor/login`;
    },
    pageRange() {
      if (!this.meta || !this.meta.last_page) return [];
      const cur = this.meta.current_page, last = this.meta.last_page;
      const range = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
      return range;
    },
  },

  mounted() {
    this.fetchVendors();
    this.fetchCategories();
    document.addEventListener('click', this.onOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onOutsideClick);
  },

  methods: {
    blankForm() {
      return {
        name: '', trading_name: '', email: '', phone: '',
        website: '', tax_id: '', rc_number: '',
        country: '', state: '', city: '', address: '', postal_code: '',
        status: 'registered', risk_rating: '', notes: '',
        category_ids: [],
        bank_name: '', bank_account_name: '', bank_account_number: '',
        bank_sort_code: '', bank_swift_code: '', bank_iban: '',
      };
    },

    async fetchVendors() {
      this.loading = true;
      try {
        const { data } = await axios.get('/procurement/vendors', {
          params: {
            search: this.search, status: this.filterStatus, page: this.page, per_page: this.perPage,
            from: this.dateFrom || undefined, to: this.dateTo || undefined,
          },
        });
        this.vendors = data.data.data;
        this.meta    = data.data;
      } catch {
        this.toast.error('Failed to load vendors');
      } finally {
        this.loading = false;
      }
    },

    async fetchCategories() {
      try {
        const { data } = await axios.get('/procurement/categories');
        this.categories = data.data;
      } catch (e) { /* non-critical */ }
    },

    debouncedFetch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.page = 1; this.fetchVendors(); }, 350);
    },

    changePage(p) {
      this.page = p;
      this.fetchVendors();
    },

    applyDateFilter() { this.page = 1; this.fetchVendors(); },
    clearDateFilter() { this.dateFrom = ''; this.dateTo = ''; this.page = 1; this.fetchVendors(); },
    openExportModal(format) {
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
      this.exportMenuOpen     = false;
    },

    getVendorValue(v, key) {
      switch (key) {
        case 'vendor_no':    return v.vendor_number || '—';
        case 'name':         return v.name || '—';
        case 'trading_name': return v.trading_name || '—';
        case 'email':        return v.email || '—';
        case 'phone':        return v.phone || '—';
        case 'status':       return this.formatStatus(v.status);
        case 'risk':         return v.risk_rating || '—';
        case 'categories':   return (v.categories || []).map(c => c.name).join(', ') || '—';
        default:             return '—';
      }
    },

    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exporting       = true;
      this.showExportModal = false;
      try {
        const { data } = await axios.get('/procurement/vendors', {
          params: { search: this.search, status: this.filterStatus, page: 1, per_page: 10000,
                    from: this.dateFrom || undefined, to: this.dateTo || undefined },
        });
        const rows = data.data?.data ?? [];
        const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(r => cols.map(c => this.getVendorValue(r, c.key)));
        const date    = new Date().toISOString().slice(0, 10);
        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `vendors-${date}.csv`; a.click();
          URL.revokeObjectURL(url);
        } else {
          const doc      = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable   = 761;
          const totalW   = cols.reduce((s, c) => s + c.pdfWidth, 0);
          const scale    = usable / totalW;
          const colStyles = {};
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
          doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 43, 69);
          doc.text('Vendor Directory', 40, 46);
          doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(122, 144, 176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);
          doc.autoTable({
            startY: 78, head: [headers], body,
            styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 43, 69], overflow: 'ellipsize' },
            headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: colStyles, tableWidth: usable, margin: { left: 40, right: 40 },
            didDrawPage(d) {
              doc.setFontSize(8); doc.setTextColor(192, 202, 214);
              doc.text(`Page ${d.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 16, { align: 'center' });
            },
          });
          doc.save(`vendors-${date}.pdf`);
        }
      } catch { this.toast?.error('Export failed. Please try again.'); }
      finally  { this.exporting = false; }
    },
    onOutsideClick(e) {
      if (this.$refs.exportWrap && !this.$refs.exportWrap.contains(e.target)) this.exportMenuOpen = false;
    },

    goToProfile(id) {
      this.$router.push(`/procurement/vendor/${id}`);
    },

    openCreate() {
      this.editingId = null;
      this.form = this.blankForm();
      this.errors = {};
      this.modalVisible = true;
    },

    openEdit(v) {
      this.editingId = v.id;
      this.form = {
        name:         v.name,
        trading_name: v.trading_name || '',
        email:        v.email || '',
        phone:        v.phone || '',
        website:      v.website || '',
        tax_id:       v.tax_id || '',
        rc_number:    v.rc_number || '',
        country:      v.country || '',
        state:        v.state || '',
        city:         v.city || '',
        address:      v.address || '',
        postal_code:  v.postal_code || '',
        status:               v.status,
        risk_rating:          v.risk_rating || '',
        notes:                v.notes || '',
        category_ids:         (v.categories || []).map(c => c.id),
        bank_name:            v.bank_name || '',
        bank_account_name:    v.bank_account_name || '',
        bank_account_number:  v.bank_account_number || '',
        bank_sort_code:       v.bank_sort_code || '',
        bank_swift_code:      v.bank_swift_code || '',
        bank_iban:            v.bank_iban || '',
      };
      this.errors = {};
      this.modalVisible = true;
    },

    closeModal() {
      this.modalVisible = false;
    },

    async saveVendor() {
      this.errors = {};
      if (!this.form.name.trim()) { this.errors.name = 'Vendor name is required'; }
      if (!this.form.email.trim()) { this.errors.email = 'Email is required'; }
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email.trim())) { this.errors.email = 'Enter a valid email address'; }
      if (!this.editingId && (!this.form.category_ids || !this.form.category_ids.length)) {
        this.errors.category_ids = 'At least one category is required';
      }
      if (Object.keys(this.errors).length) return;
      this.saving = true;
      try {
        if (this.editingId) {
          await axios.put(`/procurement/vendors/${this.editingId}`, this.form);
          this.toast.success('Vendor updated');
        } else {
          await axios.post('/procurement/vendors', this.form);
          this.toast.success('Vendor created');
        }
        this.closeModal();
        this.fetchVendors();
      } catch (e) {
        const msg = e.response?.data?.message;
        this.toast.error(msg || 'Failed to save vendor');
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(v) {
      this.deleteTarget = v;
    },

    async deleteVendor() {
      this.deleting = true;
      try {
        await axios.delete(`/procurement/vendors/${this.deleteTarget.id}`);
        this.toast.success('Vendor deleted');
        this.deleteTarget = null;
        this.fetchVendors();
      } catch {
        this.toast.error('Failed to delete vendor');
      } finally {
        this.deleting = false;
      }
    },

    switchView(view) {
      this.activeView = view;
      if (view === 'trash' && !this.trashedVendors.length) {
        this.fetchTrashed();
      }
    },

    async fetchTrashed() {
      this.trashedLoading = true;
      try {
        const { data } = await axios.get('/procurement/vendors/trashed', {
          params: { page: this.trashPage },
        });
        this.trashedVendors = data.data?.data || [];
        this.trashedMeta    = data.data ? { current_page: data.data.current_page, last_page: data.data.last_page } : null;
      } catch {
        this.toast.error('Failed to load deleted vendors');
      } finally {
        this.trashedLoading = false;
      }
    },

    changeTrashPage(p) {
      this.trashPage = p;
      this.fetchTrashed();
    },

    async restoreVendor(v) {
      this.restoringId = v.id;
      try {
        await axios.patch(`/procurement/vendors/trashed/${v.id}/restore`);
        this.toast.success(`${v.name} restored`);
        this.trashedVendors = this.trashedVendors.filter(t => t.id !== v.id);
        this.activeView = 'active';
        await this.fetchVendors();
      } catch {
        this.toast.error('Failed to restore vendor');
      } finally {
        this.restoringId = null;
      }
    },

    confirmForceDelete(v) {
      this.forceDeleteTarget = v;
    },

    async forceDeleteVendor() {
      this.forceDeleting = true;
      try {
        await axios.delete(`/procurement/vendors/trashed/${this.forceDeleteTarget.id}/force`);
        this.toast.success('Vendor permanently deleted');
        this.trashedVendors = this.trashedVendors.filter(t => t.id !== this.forceDeleteTarget.id);
        this.forceDeleteTarget = null;
      } catch {
        this.toast.error('Failed to permanently delete vendor');
      } finally {
        this.forceDeleting = false;
      }
    },

    async copyPortalUrl() {
      try {
        await navigator.clipboard.writeText(this.portalUrl);
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      } catch {
        this.toast.error('Could not copy — please copy the URL manually');
      }
    },

    generatePassword() {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
      let pwd = '';
      const arr = new Uint8Array(12);
      crypto.getRandomValues(arr);
      arr.forEach(b => { pwd += chars[b % chars.length]; });
      // Ensure at least one uppercase, one digit, one special char
      pwd = pwd.slice(0, 9)
        + 'ABCDEFGHJKLMNPQRSTUVWXYZ'[Math.floor(Math.random() * 23)]
        + '23456789'[Math.floor(Math.random() * 8)]
        + '!@#$%'[Math.floor(Math.random() * 5)];
      this.changePwForm.password = pwd;
      this.changePwForm.password_confirmation = pwd;
    },

    openChangePassword(v) {
      this.changePasswordTarget = v;
      this.changePwErrors = [];
      this.changePwForm = { password: '', password_confirmation: '' };
      this.generatePassword();
    },

    closeChangePassword() {
      this.changePasswordTarget = null;
    },

    async submitChangePassword() {
      this.changePwErrors = [];
      if (!this.changePwForm.password) {
        this.changePwErrors = ['New password is required.'];
        return;
      }
      if (this.changePwForm.password !== this.changePwForm.password_confirmation) {
        this.changePwErrors = ['Passwords do not match.'];
        return;
      }
      this.changePwLoading = true;
      try {
        await axios.patch(`/procurement/vendors/${this.changePasswordTarget.id}/reset-password`, this.changePwForm);
        this.toast.success('Vendor password updated successfully');
        this.closeChangePassword();
      } catch (e) {
        const msg = e.response?.data?.message;
        this.changePwErrors = Array.isArray(msg) ? msg : [msg || 'Failed to update password'];
      } finally {
        this.changePwLoading = false;
      }
    },

    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatStatus(s) {
      return { prospect: 'Prospect', registered: 'Registered', due_diligence: 'Due Diligence',
               qualified: 'Qualified', approved: 'Approved', suspended: 'Suspended', blocked: 'Blocked',
               in_workflow: 'In Workflow', rejected: 'Rejected' }[s] ?? s;
    },
  },
};
</script>

<style scoped>
.page-header   { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.header-actions { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.portal-bar          { display:flex; align-items:center; gap:10px; background:#f0f5ff; border:1px solid #c7d7fd; border-radius:10px; padding:10px 16px; margin-bottom:16px; flex-wrap:wrap; }
.portal-bar-icon     { color:#004AAD; font-size:13px; flex-shrink:0; }
.portal-bar-label    { font-size:12.5px; font-weight:700; color:#1a2b45; white-space:nowrap; }
.portal-bar-url      { font-size:12.5px; color:#004AAD; font-family:monospace; flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.portal-bar-copy     { display:inline-flex; align-items:center; gap:5px; padding:5px 12px; background:#fff; color:#004AAD; border:1.5px solid #c7d7fd; border-radius:6px; font-size:12px; font-weight:600; cursor:pointer; white-space:nowrap; transition:all .15s; flex-shrink:0; }
.portal-bar-copy:hover { background:#dbeafe; border-color:#93c5fd; }
.portal-bar-copy--done { background:#dcfce7; color:#15803d; border-color:#bbf7d0; }

.filter-bar   { display:flex; gap:12px; margin-bottom:16px; flex-wrap:wrap; }
.filter-input { flex:1; min-width:200px; padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; outline:none; }
.filter-input:focus { border-color:#004AAD; }
.filter-select { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; outline:none; background:#fff; cursor:pointer; }

.card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

.loading-state, .empty-state { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; color:#7a90b0; gap:10px; }
.empty-icon  { font-size:40px; color:#dde5f0; }
.empty-state p { font-size:15px; font-weight:600; color:#1a2b45; margin:0; }
.empty-state span { font-size:13px; }

.data-table  { width:100%; border-collapse:collapse; }
.data-table th { padding:11px 16px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; text-align:left; }
.data-table td { padding:13px 16px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.row-link { cursor:pointer; transition:background .12s; }
.row-link:hover { background:#f8fafc; }
.row-link:last-child td { border-bottom:none; }

.vendor-name { font-weight:600; }
.vendor-sub  { font-size:11.5px; color:#7a90b0; margin-top:2px; }
.mono        { font-family:monospace; font-size:12px; }
.text-muted  { color:#aab8cc; font-size:12px; }
.me-1        { margin-right:4px; }

.cat-badge   { display:inline-block; background:#f0f5ff; color:#004AAD; font-size:11px; font-weight:600; border-radius:12px; padding:2px 8px; margin-right:4px; }
.cat-more    { font-size:11px; color:#94a3b8; }

.status-badge { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; }
.status-badge--prospect     { background:#f1f5f9; color:#64748b; }
.status-badge--registered   { background:#eff6ff; color:#1d4ed8; }
.status-badge--due_diligence { background:#fff7ed; color:#c2410c; }
.status-badge--qualified    { background:#f0fdf4; color:#16a34a; }
.status-badge--approved     { background:#dcfce7; color:#15803d; }
.status-badge--suspended    { background:#fef9c3; color:#854d0e; }
.status-badge--blocked      { background:#fee2e2; color:#dc2626; }
.status-badge--in_workflow  { background:#ede9fe; color:#6d28d9; }
.status-badge--rejected     { background:#fee2e2; color:#dc2626; }

.risk-badge          { display:inline-block; font-size:11px; font-weight:700; border-radius:4px; padding:2px 7px; text-transform:uppercase; }
.risk-badge--low     { background:#dcfce7; color:#15803d; }
.risk-badge--medium  { background:#fef9c3; color:#854d0e; }
.risk-badge--high    { background:#fee2e2; color:#dc2626; }

.action-menu { display:flex; gap:6px; }
.btn-icon    { width:30px; height:30px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; color:#1a2b45; display:flex; align-items:center; justify-content:center; font-size:13px; cursor:pointer; transition:all .15s; }
.btn-icon:hover { background:#f4f7fb; border-color:#c5d0e0; }
.btn-icon--danger:hover { background:#fee2e2; border-color:#fecaca; color:#dc2626; }

.pagination-bar      { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-top:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.pagination-info     { font-size:13px; color:#7a90b0; }
.pagination-controls { display:flex; align-items:center; gap:4px; }
.pg-btn  { padding:6px 11px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; }
.pg-btn:hover:not(:disabled) { background:#f4f7fb; border-color:#c8d6e8; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-pages { display:flex; gap:3px; }
.pg-num  { min-width:34px; padding:6px 8px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; text-align:center; }
.pg-num:hover { background:#f4f7fb; border-color:#c8d6e8; }
.pg-num--active { background:#004AAD; border-color:#004AAD; color:#fff; font-weight:600; }
.per-page-wrap  { display:flex; align-items:center; gap:6px; }
.per-page-label { font-size:13px; color:#5a6a85; white-space:nowrap; }
.per-page-select { padding:7px 10px; border:1.5px solid #dde5f0; border-radius:7px; font-size:13px; color:#1a2b45; background:#fff; cursor:pointer; outline:none; }

/* Modal */
.modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:20px; }
.modal-box      { background:#fff; border-radius:14px; width:100%; max-width:720px; max-height:90vh; display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--sm  { max-width:440px; }
.modal-header   { display:flex; align-items:center; justify-content:space-between; padding:18px 24px; border-bottom:1px solid #e4eaf2; }
.modal-header h6 { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.btn-close-modal { background:transparent; border:none; font-size:22px; color:#7a90b0; cursor:pointer; line-height:1; }
.modal-body     { padding:24px; overflow-y:auto; flex:1; }
.modal-footer   { display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid #e4eaf2; }

.section-divider { display:flex; align-items:center; gap:10px; margin:20px 0 4px; }
.section-divider::before, .section-divider::after { content:''; flex:1; height:1px; background:#e4eaf2; }
.section-divider-label { font-size:12px; font-weight:700; color:#7a90b0; text-transform:uppercase; letter-spacing:.05em; white-space:nowrap; }

.field-grid  { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.field-full  { grid-column:1/-1; }
.field-group { display:flex; flex-direction:column; gap:5px; }
.field-label { font-size:13px; font-weight:600; color:#1a2b45; }
.req         { color:#e03535; }
.field-input { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; font-family:inherit; box-sizing:border-box; transition:border-color .15s; }
.field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.field-input--error { border-color:#e03535; }
.field-select { appearance:auto; cursor:pointer; }
.field-error  { font-size:11.5px; color:#e03535; }

.cat-checkbox-list { display:flex; flex-wrap:wrap; gap:10px; padding:8px 10px; border:1.5px solid transparent; border-radius:8px; }
.cat-checkbox-list--error { border-color:#e03535; background:#fff5f5; }
.cat-checkbox      { display:flex; align-items:center; gap:6px; font-size:13px; color:#1a2b45; cursor:pointer; }
.cat-checkbox input { cursor:pointer; }

.btn-primary  { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
.btn-primary:hover:not(:disabled) { background:#003b8e; }
.btn-primary:disabled { opacity:.5; cursor:not-allowed; }
.btn-outline  { display:inline-flex; align-items:center; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
.btn-outline:hover { background:#f4f7fb; }
.btn-danger   { display:inline-flex; align-items:center; background:#dc2626; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; }
.btn-danger:hover:not(:disabled) { background:#b91c1c; }
.btn-danger:disabled { opacity:.5; cursor:not-allowed; }

.spinner    { width:32px; height:32px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
.spinner-sm { width:14px; height:14px; border:2px solid rgba(255,255,255,.4); border-top-color:#fff; border-radius:50%; animation:spin .7s linear infinite; display:inline-block; }
@keyframes spin { to { transform:rotate(360deg); } }

.view-tabs    { display:flex; gap:4px; margin-bottom:16px; border-bottom:2px solid #e4eaf2; }
.view-tab     { background:transparent; border:none; padding:10px 18px; font-size:13.5px; font-weight:600; color:#7a90b0; cursor:pointer; border-bottom:2px solid transparent; margin-bottom:-2px; transition:all .15s; }
.view-tab--active { color:#004AAD; border-bottom-color:#004AAD; }

.btn-restore  { display:inline-flex; align-items:center; gap:6px; padding:5px 12px; background:#f0fdf4; color:#15803d; border:1px solid #bbf7d0; border-radius:7px; font-size:12.5px; font-weight:600; cursor:pointer; white-space:nowrap; transition:background .15s; }
.btn-restore:hover:not(:disabled) { background:#dcfce7; }
.btn-restore:disabled { opacity:.55; cursor:not-allowed; }

.pw-vendor-name  { font-size:14px; font-weight:600; color:#1a2b45; margin:0 0 16px; }
.pw-mail-note    { font-size:12px; color:#7a90b0; margin:14px 0 0; display:flex; align-items:center; gap:6px; }
.pw-mail-note strong { color:#1a2b45; }
.error-box  { background:#fff5f5; border-left:3px solid #e03535; border-radius:0 6px 6px 0; padding:10px 14px; margin-bottom:14px; }
.error-item { font-size:12.5px; color:#c02828; }
.field-input--mono { font-family:monospace; letter-spacing:.04em; }
.btn-regenerate { display:inline-flex; align-items:center; gap:5px; background:#f0f5ff; color:#004AAD; border:1px solid #c7d7fd; border-radius:6px; font-size:11.5px; font-weight:600; padding:3px 10px; cursor:pointer; transition:background .15s; }
.btn-regenerate:hover { background:#dbeafe; }

/* -- Column picker modal ------------------------------------------------- */
.col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
.col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
.col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
.col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
.col-picker-close:hover { color:#1a2b45; }
.col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
.col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
.col-picker-link:hover { text-decoration:underline; }
.col-picker-sep      { color:#c5d3e0; }
.col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
.col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
.col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
.col-picker-item:hover { background:#f4f7fb; }
.col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
.col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
.btn-ghost-cp   { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:1px solid #e4eaf2; cursor:pointer; font-family:inherit; }
.btn-ghost-cp:hover { background:#e4eaf2; }
.btn-primary-cp { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-cp:hover:not(:disabled) { background:#003b8e; }
.btn-primary-cp:disabled { opacity:.45; cursor:not-allowed; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity:0; }

.export-wrap{position:relative}
.btn-export{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:5px;white-space:nowrap}
.btn-export:hover{background:#e8edf5}
.btn-export:disabled{opacity:.55;cursor:not-allowed}
.export-menu{position:absolute;top:calc(100% + 6px);right:0;background:#fff;border:1.5px solid #e4eaf2;border-radius:10px;box-shadow:0 6px 20px rgba(0,0,0,.1);z-index:200;min-width:160px;overflow:hidden}
.export-opt{display:flex;align-items:center;width:100%;padding:10px 14px;background:none;border:none;font-size:13px;color:#1a2b45;cursor:pointer;text-align:left}
.export-opt:hover{background:#f4f7fb}
.btn-apply{background:#004AAD;color:#fff;border:none;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-apply:hover{background:#003b8e}
.btn-clear{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-clear:hover{background:#e8edf5}

@media(max-width:600px) {
  .field-grid { grid-template-columns:1fr; }
  .field-full { grid-column:1; }
  .modal-box  { border-radius:10px; }
}
</style>
